/* eslint-disable import/no-unresolved */
import { constants } from '@config';
import { pascalCase } from 'change-case';

import AbstractElement from './abstractElement';
import { sumArray } from 'src/utils/numbers';
import { countLeadingSpaces, countTrailingSpaces } from 'src/utils/string';
import { logWarn } from 'src/utils/logger';

const FULL_SHIFT = 1;
const HALF_SHIFT = 0.5;
const NO_SHIFT = 0;
const RIGHT_SHIFT = 1;
const LEFT_SHIFT = -1;
export default class StringRenderer extends AbstractElement {
  constructor (string, canvas, parent) {
    super();

    this.string = string;
    this.parent = parent;
    this.canvas = canvas;

    this.className = null;
    this.mainTextAnchor = null;
    this.styles = null;
    this.textBox = null;
    this.isDomAttached = null;
    this.minFontSize = null;
  }

  initialize () {
    this.textBox = this.parent.rectBBox || svgedit.utilities.getBBox(this.parent.dom.parentElement);
    this.stringBox = this.string.stringBox;
    this.hasColumns = this.parent.columns > 1;
    this.stringType = this.hasColumns ? 'Column' : 'String';
    this.isDomAttached = false;
    this.minFontSize = this.parent.font_size;

    return this;
  }

  renderString () {
    this.createStringDom();
    this.createStringClassName();
    this.calculateStringStyles();

    const html = this.calculateStringInnerHtml();
    const stringDOM = this.attachHTMLtoStringDom(html);

    this.handleStringAlignment();
    this.calculateStringPosition();

    const { listPosition, contentPosition } = this.calculateStringListPosition(stringDOM);

    // It causes force reflow for justify alignment
    this.setAttributeToDOM(stringDOM?.listDOM, 'x', listPosition);
    this.setAttributeToDOM(stringDOM?.contentDOM, 'x', contentPosition);

    this.setAttributeToDOM(this.string.dom, 'x', this.string.x);
    this.setAttributeToDOM(this.string.dom, 'dx', this.dx);

    this.attachStringDomToParent();

    return this.string.dom;
  }

  createStringDom () {
    if (this.string.dom) return;
    this.string.dom = document.createElementNS(svgedit.NS.SVG, 'text');
  }

  createStringClassName () {
    this.className = '';

    if (this.string.isBlank) this.className += ' blank';
    if (this.string.isEol) this.className += ' eol';
  }

  calculateStringStyles () {
    this.string.endIndex = this.string.startIndex + this.string.textContent.length;
    if (this.parent.heading > 0) {
      this.string.heading = 0;
    }

    this.mainTextAnchor = this.string.textAnchor || this.parent.text_anchor;
    this.affectedTextAnchor = !this.string.textAnchor || this.string.textAnchor?.includes('justify')
      ? this.parent.text_anchor
      : this.string.textAnchor;

    // The factor will determine the flow of content in a line
    // If factor is 1, the text will flow from left to right
    // If factor is -1, the text will flow from right to left
    // The flow depends on the direction and alignment of text
    this.factor = this.#calculateLineFactor();

    this.modifier = this.parent.direction === 'rtl' ? LEFT_SHIFT : RIGHT_SHIFT;
    this.wordSpacing = this.parent.word_spacing || 0;

    this.rightMargin = this.string.isNewLine && this.string.first_line_margin && this.factor < 1
      ? this.string.first_line_margin
      : this.string.right_margin;

    this.margin = this.string.isNewLine && this.string.first_line_margin && this.factor > -1
      ? this.string.first_line_margin
      : this.string.line_margin;

    // Fot RTL text, disable using letter spacing
    if (this.parent.direction === 'rtl') {
      this.parent.letter_spacing = 0;
      if (this.mainTextAnchor.includes('justify')) this.string.textAnchor = '';
    }

    this.styles = this.string.getStringStyles(this.parent.styles);
    this.string.styles = this.styles;

    const lineFontSizes = this.styles?.filter(style => style.attribute === 'font-size').map(style => style.value);

    this.minFontSize = lineFontSizes?.length ? Math.min(...lineFontSizes) : this.minFontSize;
  }

  #calculateLineFactor () {
    const mainTextAnchor = this.mainTextAnchor;
    const reverseFactor = this.parent.direction === 'rtl' ? -1 : 1;
    const alignmentFactor = {
      start: reverseFactor,
      end: -reverseFactor,
      middle: 1,
      justify_all: 1,
      justify_start: 1,
      justify_end: -1,
      justify_middle: 1
    };

    return Number.isNaN(alignmentFactor[mainTextAnchor])
      ? 1
      : alignmentFactor[mainTextAnchor];
  }

  calculateStringInnerHtml () {
    if (this.string.isBlank) return this.string.textContent;
    if (this.styles.length === 0) return this.string.textContent;

    const parent = this.parent;
    const spans = [];
    const addedStyles = [];

    for (const style of this.styles) {
      const position = { start: style.start, end: style.end };
      const isAdded = addedStyles.find(style => {
        return style.start === position.start && style.end === position.end;
      });

      const content = this.string.textContent.substring(style.start, style.end);

      isAdded || addedStyles.push(position);
      const span = isAdded && spans.length
        ? spans.pop()
        : document.createElementNS(svgedit.NS.SVG, 'tspan');

      span.setAttribute('xml:space', 'preserve');

      if (style.attribute) {
        this.setSpanCustomFontId(span, style);

        if (style.attribute === 'link') {
          if (span.textContent) span.textContent = '';

          const fill = span.getAttribute('fill') || parent.fill;
          const existingWrapper = span.querySelector('a');
          const linkWrapper = existingWrapper || document.createElement('a');

          this.setAttributeToDOM(linkWrapper, 'xlink:href', style.value);
          this.setAttributeToDOM(linkWrapper, 'fill', fill);

          linkWrapper.textContent = content;
          existingWrapper || span.appendChild(linkWrapper);
        } else {
          const linkWrapper = span.querySelector('a');
          const fill = style.attribute === 'fill' && style.value;

          // SVG bug: a tag cannot inherit fill attribute from parent
          this.setAttributeToDOM(linkWrapper, 'fill', fill);
          this.setAttributeToDOM(span, style.attribute, style.value);

          linkWrapper ? linkWrapper.textContent = content : span.textContent = content;
        }
        spans.push(span);
      } else if (content) {
        span.textContent = content;
        spans.push(span);
      }
    }

    return spans.reduce((acc, next) => acc + next.outerHTML, '');
  }

  setSpanCustomFontId (span, style) {
    if (style.attribute !== 'font-family') return;

    this.parent.customFontIdHash[style.value] = this.parent.customFontIdHash[style.value] || INKIVE.FontsFactory.get(style.value);

    this.setAttributeToDOM(span, 'custom-font-id', this.parent.customFontIdHash[style.value]?.customId);
  }

  attachHTMLtoStringDom (html) {
    const string = this.string;
    if (!string.bulletLevel && !string.orderedLevel) {
      if (html.includes('<tspan')) {
        string.dom.innerHTML = html;
      } else {
        string.dom.textContent = html;
      }
      return;
    }

    // Handle bullet and ordered list
    const listDOM = string.bulletLevel ? this.createBulletListDOM() : this.createOrderedListDOM();
    const contentDOM = new INKIVE.Text.Span().setSimpleText(html).render(this.canvas);
    this.styles.length || contentDOM.setAttribute('xml:space', 'preserve');

    string.dom.appendChild(listDOM);
    string.dom.appendChild(contentDOM);

    return { listDOM, contentDOM };
  }

  createBulletListDOM () {
    const string = this.string;
    const canvas = this.canvas;
    const bulletFontFamily = string.isNewLine && string.bulletFontFamily
      ? string.bulletFontFamily
      : constants.text.bulletPoints.fontFamily;
    const list = string.isNewLine
      ? new INKIVE.Text.Span().setDefaultBullet(string.bulletContent).render(canvas)
      : new INKIVE.Text.Span().setDefaultIndent().render(canvas);

    this.setAttributeToDOM(list, 'xml:space', 'preserve');
    this.setAttributeToDOM(list, 'text-anchor', 'start');
    this.setAttributeToDOM(list, 'data-bullet-width', string.bulletWidth);
    this.setAttributeToDOM(list, 'fill', string.bulletFill);
    this.setAttributeToDOM(list, 'font-size', string.bulletFontSize || this.minFontSize);
    this.setAttributeToDOM(list, 'custom-font-id', string.bulletCustomFontId);
    this.setAttributeToDOM(list, 'font-family', bulletFontFamily);
    this.setAttributeToDOM(list, 'font-style', string.bulletFontStyle);
    this.setAttributeToDOM(list, 'highlight', string.bulletHighlight);

    return list;
  }

  createOrderedListDOM () {
    const string = this.string;
    const canvas = this.canvas;
    const orderedFontFamily = string.isNewLine && string.orderedFontFamily
      ? string.orderedFontFamily
      : null;

    const list = string.isNewLine
      ? new INKIVE.Text.Span().setListItem(`${string.order}`).render(canvas)
      : new INKIVE.Text.Span().setListIndent().render(canvas);

    this.setAttributeToDOM(list, 'xml:space', 'preserve');
    this.setAttributeToDOM(list, 'text-anchor', 'start');
    this.setAttributeToDOM(list, 'data-ordered-width', string.orderedWidth);
    this.setAttributeToDOM(list, 'fill', string.orderedFill);
    this.setAttributeToDOM(list, 'font-size', string.orderedFontSize || this.minFontSize);
    this.setAttributeToDOM(list, 'font-weight', string.orderedFontWeight);
    this.setAttributeToDOM(list, 'highlight', string.orderedHighlight);
    this.setAttributeToDOM(list, 'font-style', string.orderedFontStyle);
    this.setAttributeToDOM(list, 'custom-font-id', string.orderedCustomFontId);
    this.setAttributeToDOM(list, 'font-family', orderedFontFamily);

    return list;
  }

  setAttributeToDOM (dom, attribute, value) {
    if (!dom || !value) return;

    dom.setAttribute(attribute, value);
  }

  calculateStringBox () {
    if (this.stringBox) return;

    // if stringBox is not available, it will cause performance issue
    logWarn('calculateStringBox', { message: 'Slow rendering detected' });
    if (this.string.orderedLevel || this.string.bulletLevel) {
      this.stringBox = this.string.dom.querySelector('tspan:not(.ordered):not(.bullets)').getBBox();
    } else {
      this.stringBox = this.string.dom.getBBox();
    }
  }

  handleStringAlignment () {
    this.calculateStringBox();

    if (this.mainTextAnchor.includes('justify')) {
      this.wordSpacing = 0;
      this.attachStringDomToParent();
      this.dx = this.calculateDxByJustify();
    } else {
      this.dx = this.calculateDxByParent();
    }
  }

  getStringLetterSpacing () {
    if (Array.isArray(this.parent.letter_spacing)) {
      return this.parent.letter_spacing.slice(this.string.startIndex, this.string.endIndex);
    }
    return this.parent.letter_spacing;
  }

  calculateDxByParent () {
    if (!this.wordSpacing && !this.parent.letter_spacing) return '';

    const listDx = this.calculateListDx();
    const dx = Array.isArray(this.parent.letter_spacing)
      ? this.parent.letter_spacing.slice(this.string.startIndex, this.string.endIndex)
      : new Array(this.string.textContent.length).fill(this.parent.letter_spacing || 0);

    if (dx[0]) dx[0] = 0;

    this.addWordSpacing(dx, this.wordSpacing, this.string.textContent);
    return listDx.concat(dx).join(' ');
  }

  addWordSpacing (dx, wordSpacing, content) {
    dx[0] = 0;
    if (!wordSpacing) return;

    const leadingSpaces = countLeadingSpaces(content);
    const trailingSpaces = countTrailingSpaces(content);

    for (let i = leadingSpaces; i < content.length - trailingSpaces; i++) {
      if (!/\s/.test(content[i])) continue;
      dx[i] = wordSpacing; // Add dx for space
    }
  }

  calculateDxByJustify () {
    if (this.string.isEol && this.mainTextAnchor !== 'justify_all') return '';

    const line = this.string.textContent.trim();
    const numOfSpace = line.split(/\s/).length - 1;
    const columnGap = this.hasColumns ? this.parent.columns_gap || 0 : 0;
    const listWidth = this.string.orderedLevel ? this.getStringOrderedWidth() : this.getStringBulletWidth();
    const spaceToFill = (this.textBox.width - columnGap * (this.parent.columns - 1)) / this.parent.columns - this.stringBox.width - listWidth - this.margin - this.rightMargin - this.wordSpacing * numOfSpace;
    const wordSpacing = numOfSpace ? spaceToFill / numOfSpace : 0;
    const dx = new Array(this.string.textContent.length).fill(0);
    const listDx = this.calculateListDx();

    this.addWordSpacing(dx, wordSpacing, this.string.textContent);

    return listDx.concat(dx).join(' ');
  }

  calculateListDx () {
    const listSymbolLength = this.getListSymbolLength();

    return new Array(listSymbolLength).fill(0);
  }

  getListSymbolLength () {
    if (!this.string.orderedLevel && !this.string.bulletLevel) return 0;
    if (!this.string.isNewLine) return 1;

    if (this.string.bulletLevel) {
      return this.string.bulletContent?.length || 1;
    }

    if (this.string.orderedLevel) {
      return this.string.order?.length + 1 || 1;
    }

    return 0;
  }

  attachStringDomToParent () {
    if (this.isDomAttached) return;

    this.isDomAttached = true;

    // Set as many attributes as possible before appending to browser DOM to avoid force reflow
    this.setAttributeToDOM(this.string.dom, 'class', this.className);
    this.setAttributeToDOM(this.string.dom, 'y', this.string.y);
    this.setAttributeToDOM(this.string.dom, 'dx', this.dx);
    this.setAttributeToDOM(this.string.dom, 'text-anchor', this.string.textAnchor);
    this.setAttributeToDOM(this.string.dom, 'data-heading', this.string.heading);
    this.setAttributeToDOM(this.string.dom, 'data-bullet', this.string.bulletLevel);
    this.setAttributeToDOM(this.string.dom, 'data-bullet-style', this.string.bulletStyle);
    this.setAttributeToDOM(this.string.dom, 'data-ordered', this.string.orderedLevel);
    this.setAttributeToDOM(this.string.dom, 'data-ordered-style', this.string.orderedStyle);
    this.setAttributeToDOM(this.string.dom, 'data-ordered-numbering-style', this.string.orderedNumberingStyle);
    this.setAttributeToDOM(this.string.dom, 'data-ordered-regex', this.string.orderedRegex);
    this.setAttributeToDOM(this.string.dom, 'data-ordered-start', this.string.orderedStart);

    this.parent.dom.appendChild(this.string.dom);
  }

  calculateStringPosition () {
    this.string.x = this[`handle${this.stringType}Align${pascalCase(this.mainTextAnchor)}`]?.() || this.string.x;
  }

  calculateStringListPosition (stringDOM) {
    const defaultPosition = {
      contentPosition: null,
      listPosition: null
    };

    if (!stringDOM) return defaultPosition;

    const { listDOM, contentDOM } = stringDOM;
    if (!contentDOM) return defaultPosition;
    if (!listDOM) return defaultPosition;

    const string = this.string;
    const shiftContentFactor = this.calculateContentAlignmentFactor();

    const listWidth = string.orderedLevel ? this.getStringOrderedWidth() : this.getStringBulletWidth();

    const contentPosition = string.x + this.modifier * shiftContentFactor * listWidth;
    const listPosition = this.calculateListPosition(contentPosition, listWidth);

    return { contentPosition, listPosition, listWidth };
  }

  calculateLeftToRightListPosition (contentPosition, listWidth) {
    if (this.modifier !== RIGHT_SHIFT) return null;

    const totalLetterSpacing = sumArray(this.dx.split(' ').map(Number) || 0);
    const textWidth = this.stringBox.width + totalLetterSpacing;
    if (this.mainTextAnchor === 'middle') {
      return contentPosition - textWidth / 2 - listWidth;
    }

    if (this.mainTextAnchor === 'justify_middle') {
      if (this.affectedTextAnchor === 'middle') return contentPosition - textWidth / 2 - listWidth;
      if (this.affectedTextAnchor === 'end') return contentPosition - textWidth - listWidth;

      return contentPosition - listWidth;
    }

    if (this.mainTextAnchor === 'end') {
      return contentPosition - textWidth - listWidth;
    }

    if (this.mainTextAnchor === 'justify_end') {
      if (this.affectedTextAnchor === 'middle') return contentPosition - textWidth / 2 - listWidth;
      if (this.affectedTextAnchor === 'end') return contentPosition - textWidth - listWidth;

      return contentPosition - listWidth;
    }
  }

  calculateListPosition (contentPosition, listWidth) {
    const leftToRightListPosition = this.calculateLeftToRightListPosition(contentPosition, listWidth);
    if (leftToRightListPosition !== null) return leftToRightListPosition;

    const factorMargin = this.modifier === RIGHT_SHIFT
      ? this.mainTextAnchor.includes('end') ? LEFT_SHIFT : RIGHT_SHIFT
      : this.mainTextAnchor.includes('end') ? NO_SHIFT : LEFT_SHIFT;

    const factorWidth = this.modifier === RIGHT_SHIFT
      ? this.mainTextAnchor.includes('end') ? RIGHT_SHIFT : NO_SHIFT
      : this.mainTextAnchor.includes('end') ? RIGHT_SHIFT : RIGHT_SHIFT;

    if (this.hasColumns) {
      const { baseRtlColumnPosition, columnWidth } = this.calculateColumnData();
      return baseRtlColumnPosition + factorWidth * columnWidth + factorMargin * this.margin;
    }

    return this.textBox.x + factorWidth * this.textBox.width + factorMargin * this.margin;
  }

  calculateContentAlignmentFactor () {
    if (this.mainTextAnchor === 'start') return RIGHT_SHIFT * FULL_SHIFT;
    if (this.mainTextAnchor === 'middle') return RIGHT_SHIFT * HALF_SHIFT;
    if (this.mainTextAnchor === 'end') return this.modifier === RIGHT_SHIFT ? LEFT_SHIFT * NO_SHIFT : LEFT_SHIFT * NO_SHIFT;

    if (this.mainTextAnchor === 'justify_all') {
      if (this.affectedTextAnchor === 'middle') return RIGHT_SHIFT * HALF_SHIFT;
      if (this.affectedTextAnchor === 'end') return RIGHT_SHIFT * NO_SHIFT;

      return RIGHT_SHIFT * FULL_SHIFT;
    }

    if (this.mainTextAnchor === 'justify_start' || this.mainTextAnchor === 'justify') {
      if (this.string.isEol) return RIGHT_SHIFT * FULL_SHIFT;
      if (this.affectedTextAnchor === 'middle') return RIGHT_SHIFT * HALF_SHIFT;
      if (this.affectedTextAnchor === 'end') return RIGHT_SHIFT * NO_SHIFT;

      return RIGHT_SHIFT * FULL_SHIFT;
    }

    if (this.mainTextAnchor === 'justify_middle') {
      if (this.string.isEol) return RIGHT_SHIFT * HALF_SHIFT;
      if (this.affectedTextAnchor === 'middle') return RIGHT_SHIFT * HALF_SHIFT;
      if (this.affectedTextAnchor === 'end') return RIGHT_SHIFT * NO_SHIFT;

      return RIGHT_SHIFT * FULL_SHIFT;
    }

    if (this.mainTextAnchor === 'justify_end') {
      if (this.string.isEol) return LEFT_SHIFT * NO_SHIFT;
      if (this.affectedTextAnchor === 'middle') return RIGHT_SHIFT * HALF_SHIFT;
      if (this.affectedTextAnchor === 'end') return LEFT_SHIFT * NO_SHIFT;

      return RIGHT_SHIFT * FULL_SHIFT;
    }

    return RIGHT_SHIFT * FULL_SHIFT;
  }

  getStringOrderedWidth () {
    if (!this.string.orderedLevel) return 0;
    return this.parent.calculateOrderedEntryWidth(this.string.order, this.parent.font_size, this.string.orderedWidth, this.string.orderedLevel, this.string.orderedStyle);
  }

  getStringBulletWidth () {
    if (!this.string.bulletLevel) return 0;
    return this.string.bulletWidth || this.parent.font_size;
  }

  handleStringAlignStart () {
    return this.parent.direction === 'rtl'
      ? this.textBox.x + this.textBox.width - this.margin
      : this.textBox.x + this.margin;
  }

  handleStringAlignMiddle () {
    return this.textBox.x + this.textBox.width / 2 + this.margin / 2 - this.rightMargin / 2;
  }

  handleStringAlignEnd () {
    return this.parent.direction === 'rtl'
      ? this.textBox.x + this.margin
      : this.textBox.x + this.textBox.width - this.margin;
  }

  handleStringAlignJustifyAll () {
    if (this.parent.text_anchor === 'middle') {
      return this.textBox.x + this.textBox.width / 2 * this.modifier + this.modifier * this.margin / 2 - this.modifier * this.rightMargin / 2;
    } else if (this.parent.text_anchor === 'end') {
      return this.textBox.x + this.textBox.width * this.modifier - this.modifier * this.rightMargin;
    }
    return this.textBox.x + this.modifier * this.margin;
  }

  handleStringAlignJustifyStart () {
    if (this.parent.text_anchor === 'middle') {
      return this.string.isEol
        ? this.textBox.x + this.textBox.width / 2 * this.modifier - this.textBox.width / 2 + this.stringBox.width / 2 + this.modifier * this.margin
        : this.textBox.x + this.textBox.width / 2 * this.modifier + this.modifier * this.margin / 2 - this.modifier * this.rightMargin / 2;
    } else if (this.parent.text_anchor === 'end') {
      return this.string.isEol
        ? this.textBox.x + this.textBox.width * this.modifier - this.textBox.width + this.stringBox.width + this.modifier * this.margin
        : this.textBox.x + this.textBox.width * this.modifier - this.modifier * this.rightMargin;
    }
    return this.textBox.x + this.modifier * this.margin;
  }

  handleStringAlignJustifyMiddle () {
    if (this.parent.text_anchor === 'middle') {
      return this.textBox.x + this.textBox.width / 2 * this.modifier + this.margin / 2 - this.rightMargin / 2;
    } else if (this.parent.text_anchor === 'end') {
      return this.string.isEol
        ? this.textBox.x + this.textBox.width * this.modifier - this.textBox.width / 2 + this.stringBox.width / 2 + this.margin / 2 - this.rightMargin / 2
        : this.textBox.x + this.textBox.width * this.modifier - this.modifier * this.rightMargin;
    } else if (this.string.isEol) {
      return this.textBox.x + this.textBox.width / 2 - this.stringBox.width / 2 + this.margin / 2 - this.rightMargin / 2;
    }
    return this.textBox.x + this.margin;
  }

  handleStringAlignJustifyEnd () {
    if (this.parent.text_anchor === 'middle') {
      return this.string.isEol
        ? this.textBox.x + this.textBox.width / 2 * this.modifier + this.textBox.width / 2 - this.stringBox.width / 2 - this.modifier * this.margin
        : this.textBox.x + this.textBox.width / 2 * this.modifier - this.modifier * this.margin / 2 + this.modifier * this.rightMargin / 2;
    } else if (this.parent.text_anchor === 'end') {
      return this.textBox.x + this.textBox.width * this.modifier - this.modifier * this.margin;
    } else if (this.string.isEol) {
      return this.textBox.x + this.textBox.width - this.stringBox.width - this.modifier * this.margin;
    }
    return this.textBox.x + this.modifier * this.rightMargin;
  }

  calculateColumnData () {
    const columnGap = this.parent.columns_gap || 0;
    const columnWidth = (this.textBox.width - columnGap * (this.parent.columns - 1)) / this.parent.columns;
    const columnIndex = this.string.columnIndex;
    const modifier = this.parent.direction === 'rtl' ? -1 : 1;
    const baseColumnPosition = this.textBox.x + (columnGap + columnWidth) * columnIndex;
    const baseRtlColumnPosition = this.textBox.x + (columnGap + columnWidth) * columnIndex * modifier;

    return {
      columnGap,
      columnWidth,
      modifier,
      baseColumnPosition,
      baseRtlColumnPosition
    };
  }

  handleColumnAlignStart () {
    const { baseRtlColumnPosition } = this.calculateColumnData();
    return this.parent.direction === 'rtl'
      ? baseRtlColumnPosition + this.textBox.width - this.margin
      : baseRtlColumnPosition + this.margin;
  }

  handleColumnAlignMiddle () {
    const { baseRtlColumnPosition, columnGap, columnWidth } = this.calculateColumnData();
    const offset = !this.hasColumns ? columnGap : columnGap * (this.parent.columns - 1) + columnWidth * (this.parent.columns - 1);
    return this.parent.direction === 'rtl'
      ? baseRtlColumnPosition + columnWidth / 2 + offset + this.margin / 2 - this.rightMargin / 2
      : baseRtlColumnPosition + columnWidth / 2 + this.margin / 2 - this.rightMargin / 2;
  }

  handleColumnAlignEnd () {
    const { baseRtlColumnPosition, columnGap, columnWidth } = this.calculateColumnData();
    const offset = !this.hasColumns ? columnGap : columnGap * (this.parent.columns - 1) + columnWidth * (this.parent.columns - 2);
    return this.parent.direction === 'rtl'
      ? baseRtlColumnPosition + columnWidth + offset + this.margin
      : baseRtlColumnPosition + columnWidth - this.margin;
  }

  handleColumnAlignJustifyAll () {
    const { baseColumnPosition, columnWidth } = this.calculateColumnData();
    if (this.parent.text_anchor === 'middle') {
      return baseColumnPosition + columnWidth / 2 + this.margin / 2 - this.rightMargin / 2;
    } else if (this.parent.text_anchor === 'end') {
      return baseColumnPosition + columnWidth - this.rightMargin;
    }
    return baseColumnPosition + this.margin;
  }

  handleColumnAlignJustifyStart () {
    const { baseColumnPosition, columnWidth } = this.calculateColumnData();
    if (this.parent.text_anchor === 'middle') {
      return this.string.isEol
        ? baseColumnPosition + this.stringBox.width / 2 + this.margin
        : baseColumnPosition + columnWidth / 2 + this.margin / 2 - this.rightMargin / 2;
    } else if (this.parent.text_anchor === 'end') {
      return this.string.isEol
        ? baseColumnPosition + this.stringBox.width + this.margin
        : baseColumnPosition + columnWidth - this.rightMargin / 2;
    }

    return baseColumnPosition + this.margin;
  }

  handleColumnAlignJustifyMiddle () {
    const { baseColumnPosition, columnWidth } = this.calculateColumnData();
    if (this.parent.text_anchor === 'middle') {
      return baseColumnPosition + columnWidth / 2 + this.margin / 2 - this.rightMargin / 2;
    } else if (this.parent.text_anchor === 'end') {
      return this.string.isEol
        ? baseColumnPosition + columnWidth / 2 + this.stringBox.width / 2 + this.margin / 2 - this.rightMargin / 2
        : baseColumnPosition + columnWidth - this.rightMargin;
    } else if (this.string.isEol) {
      return baseColumnPosition + columnWidth / 2 - this.stringBox.width / 2 + this.margin / 2 - this.rightMargin / 2;
    }
    return baseColumnPosition + this.margin;
  }

  handleColumnAlignJustifyEnd () {
    const { baseColumnPosition, columnWidth } = this.calculateColumnData();
    if (this.parent.text_anchor === 'middle') {
      return this.string.isEol
        ? baseColumnPosition + columnWidth - this.stringBox.width / 2 - this.margin
        : baseColumnPosition + columnWidth / 2 - this.margin / 2 + this.rightMargin / 2;
    } else if (this.parent.text_anchor === 'end') {
      return baseColumnPosition + columnWidth - this.margin;
    } else if (this.string.isEol) {
      return baseColumnPosition + columnWidth - this.stringBox.width - this.margin;
    }
    return baseColumnPosition + this.rightMargin;
  }
}
